/* eslint-disable tree-shaking/no-side-effects-in-initialization */
import { groupBy } from "lodash";
import { zReadonlyArray } from "../zod/utils.js";
import { z } from "zod";

import { VariableName } from "../typeBrands.js";

export function mergeParamReferences(
  ...refs: Array<readonly ParamReference[]>
): ParamReference[] {
  const groupedByParam = groupBy(refs.flat(), (ref) => ref.param);
  return Object.values(groupedByParam).map((group) => {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const { param } = group[0]!;
    return {
      param,
      locations: group.flatMap(({ locations }) => locations),
    };
  });
}

export const SourcePosition = z.object({
  line: z.number(),
  column: z.number(),
});
export type SourcePosition = z.infer<typeof SourcePosition>;

export const SourceRange = z.tuple([SourcePosition, SourcePosition]);
export type SourceRange = z.infer<typeof SourceRange>;

export const SourceLocation = SourcePosition.and(
  z
    .object({
      length: z.union([z.number(), z.null()]),
    })
    .partial(),
);
export type SourceLocation = z.infer<typeof SourceLocation>;

export const ParamReferenceKey = z.union([z.string(), z.number()]);
export type ParamReferenceKey = z.infer<typeof ParamReferenceKey>;

export const ParamReference = z.object({
  param: VariableName,
  locations: zReadonlyArray(SourceLocation),
  referencedKeys: zReadonlyArray(ParamReferenceKey).nullable().optional(),
});
export type ParamReference = z.infer<typeof ParamReference>;

export const CellReferencesV1 = z.object({
  referencedParams: zReadonlyArray(VariableName),
  newParams: zReadonlyArray(VariableName),
});

export type CellReferencesV1 = z.infer<typeof CellReferencesV1>;

export const FunctionDefinition = z.object({
  name: z.string(),
  src: z.string(),
});
export type FunctionDefinition = z.infer<typeof FunctionDefinition>;

export const PythonCellReferencesV1 = CellReferencesV1.extend({
  imports: zReadonlyArray(VariableName),
  importStrings: zReadonlyArray(z.string()),
  functions: zReadonlyArray(FunctionDefinition),
});
export type PythonCellReferencesV1 = z.infer<typeof PythonCellReferencesV1>;

export const SqlCellReferencesV1 = CellReferencesV1.extend({
  containsBlocks: z.boolean(),
});
export type SqlCellReferencesV1 = z.infer<typeof SqlCellReferencesV1>;

export const CellReferencesParseError = z.object({
  error: z.string(),
});

export type CellReferencesParseError = z.infer<typeof CellReferencesParseError>;

export const ParsedPythonCellReferencesV1 = z.union([
  PythonCellReferencesV1,
  CellReferencesParseError,
]);
export type ParsedPythonCellReferencesV1 = z.infer<
  typeof ParsedPythonCellReferencesV1
>;

export const FormatSourceError = z.object({
  error: z.string(),
});
export type FormatSourceError = z.infer<typeof FormatSourceError>;

export const FormatSourceResponse = z.union([z.string(), FormatSourceError]);
export type FormatSourceResponse = z.infer<typeof FormatSourceResponse>;

export const SplitSqlResponse = z.object({
  statements: zReadonlyArray(z.string()),
});
export type SplitSqlResponse = z.infer<typeof SplitSqlResponse>;

export const CteInfo = z.object({
  name: z.string(),
  query: z.string(),
});
export type CteInfo = z.infer<typeof CteInfo>;

export const CteSplitResponse = z.object({
  ctes: zReadonlyArray(CteInfo),
  mainQuery: z.string(),
  hasCtes: z.boolean(),
  error: z.union([z.string(), z.null()]).optional(),
});
export type CteSplitResponse = z.infer<typeof CteSplitResponse>;

export const HqlToSqlErrorResponse = z.object({
  message: z.string(),
  is_user_facing: z.boolean(),
  underlying_error: z.string().optional(),
});

export type HqlToSqlErrorResponse = z.infer<typeof HqlToSqlErrorResponse>;

export const HqlToSqlPositionalResult = z.object({
  sql: z.string(),
  parameters: zReadonlyArray(
    z.object({
      name: z.string(),
      type: z.string(),
    }),
  ),
});

export type HqlToSqlPositionalResult = z.infer<typeof HqlToSqlPositionalResult>;
